import { Module } from '../Module';

// Delete when removing settingsReactMigration2 feature flag
interface IDragAndDropEvent extends PopStateEvent, DragEvent {
}

/**
* Adds behavior for attaching and handling drag and drop events for user files.
* Attr value is callback function on file drop event. This callback function
* receives one parameter, which is named 'file'.
*
* In order to use multiple tbFileDrop elements per page, each tbFileDrop element
* must have its own unique id attribute.
*
* Example usage:
*  div(tb-file-drop="onFileDropped(file)")
*  div(tb-file-drop="onFileDropped()")
*
* Style hooks:
*   '.tb-file-drop-dragging': if user is dragging file over element
**/

Module.instance().directive('tbFileDrop', <any[]>[
  '$parse', '$timeout',
  ($parse: ng.IParseService, $timeout: ng.ITimeoutService) => {
    const DRAGGING_CLASS = 'tb-file-drop-dragging';
    return {
      restrict: 'A',
      link: ($scope: ng.IScope, element: JQuery, attr: ng.IAttributes) => {
        let dragLeaveTimeout: ng.IPromise<any>;

        const onDropFn = $parse(attr['tbFileDrop']);
        const eventNamespace = '.tbFileDrop.' + (element.attr('id') || $scope.$id);
        const doc = angular.element(document);

        const setDropEffect = (event: JQuery.TriggeredEvent, effect: string) => {
          // Adds OS cursor feedback on some browsers
          const originalEvent = event.originalEvent as IDragAndDropEvent;
          if (originalEvent && originalEvent.dataTransfer) {
            originalEvent.dataTransfer.dropEffect = effect;
          }
        };

        element.on(`dragenter${eventNamespace} dragover${eventNamespace}`, (event: JQuery.TriggeredEvent): void => {
          event.stopPropagation();
          event.preventDefault();
          if ($(event.target).closest(element).length) {
            $timeout.cancel(dragLeaveTimeout);
            element.addClass(DRAGGING_CLASS);
            setDropEffect(event, 'copy');
          }
        });

        element.on(`dragleave${eventNamespace} dragend${eventNamespace}`, (event: JQuery.Event): void => {
          event.stopPropagation();
          event.preventDefault();
          $timeout.cancel(dragLeaveTimeout);
          dragLeaveTimeout = $timeout(() => {
            element.removeClass(DRAGGING_CLASS);
          }, 20, false); // 20 ms to avoid flash when dragging from parent (dragleave) to child elm (dragenter)
        });

        element.on(`drop${eventNamespace}`, (event: JQuery.TriggeredEvent): void => {
          event.stopPropagation();
          event.preventDefault();
          // Dropped file inside drop area
          if ($(event.target).closest(element).length) {
            $timeout.cancel(dragLeaveTimeout);
            element.removeClass(DRAGGING_CLASS);
            $scope.$apply(() => {
              const originalEvent = event.originalEvent as IDragAndDropEvent;
              if (originalEvent &&
                  originalEvent.dataTransfer &&
                  originalEvent.dataTransfer.files &&
                  originalEvent.dataTransfer.files[0]) {
                onDropFn($scope, {file: originalEvent.dataTransfer.files[0]});
              }
            });
          }
        });

        // Disable dragging (and indicate with cursor) outside of the tbFileDrop element(s)
        doc.on(`dragenter${eventNamespace} dragover${eventNamespace}`, (event: JQuery.TriggeredEvent): void => {
          event.stopPropagation();
          event.preventDefault();
          setDropEffect(event, 'none');
        });

        // Disable dropping outside of the tbFileDrop element(s)
        doc.on(`drop${eventNamespace}`, (event: JQuery.Event): void => {
          event.stopPropagation();
          event.preventDefault();
        });

        // Remove the drag+drop event listeners from the doc when the tbFileDrop element has been destroyed
        element.on('$destroy', () => {
          doc.off(eventNamespace);
        });
      }
    };
  }
]);
